import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "./ConfirmationModal";
import GenericSelectTailwind from "./GenericSelectTailwind";
import Toast from "./Toast";
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import { categoriesService, ageGroupsService, pdfExportService, groupsService } from '../../services';
import settingsService from '../../services/settingsService';
import cacheService from '../../utils/cacheService';

const ScheduleTailwind = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDay, setSelectedDay] = useState(null); // Tag-Auswahl (muss ausgewählt sein)
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);

  // Venue-Struktur
  const [venuesData, setVenuesData] = useState({
    "Fläche A": [],
    "Fläche B": []
  });

  // State für Änderungsverfolgung
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [changeHistory, setChangeHistory] = useState([]);

  // Drag & Drop State für Events zwischen Venues
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [draggedFromVenue, setDraggedFromVenue] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverVenue, setDragOverVenue] = useState(null);

  const [teamData, setTeamData] = useState(null); // Team-Daten für Team-Events
  const [expandedNations, setExpandedNations] = useState(new Set()); // Erweiterte Nationen in Team-Events
  const [partnerTeamsData, setPartnerTeamsData] = useState(new Map()); // Partner-Teams-Daten pro Gruppe (Map von groupId -> { partnerTeams: [...] })
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [hasExistingEvents, setHasExistingEvents] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState('generate');
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [isStartingTournament, setIsStartingTournament] = useState(false);

  // PDF Export States
  const [isExporting, setIsExporting] = useState(false);

  // Timeline View State
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'timeline'

  // Context Menu State
  const [contextMenu, setContextMenu] = useState(null); // { x, y, event, venue, index }
  const [contextMenuSubmenu, setContextMenuSubmenu] = useState(null); // 'venue-change' | 'day-change' | null
  const [contextMenuDaySubmenu, setContextMenuDaySubmenu] = useState(null); // selected day for day-change submenu

  // More Menu State
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Bestätigungsdialog für Edit-Mode Abbruch
  const [showCancelEditConfirmation, setShowCancelEditConfirmation] = useState(false);

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadScheduleData();
    loadParticipants();
    loadCategoriesAndAgeGroups();
    checkExistingEvents();
  }, []);

  // Keyboard-Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignoriere Shortcuts wenn ein Input-Feld fokussiert ist
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      // E: Edit-Mode toggle
      if (e.key === 'e' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        handleToggleEditMode();
        return;
      }

      // Ctrl+S / Cmd+S: Speichern (nur im Edit-Mode)
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && isEditMode) {
        e.preventDefault();
        if (hasUnsavedChanges && !isSaving) {
          handleSaveChanges();
        }
        return;
      }

      // Escape: Edit-Mode beenden / Context-Menu schließen
      if (e.key === 'Escape') {
        if (contextMenu) {
          handleCloseContextMenu();
        } else if (isEditMode) {
          handleToggleEditMode();
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, hasUnsavedChanges, isSaving, contextMenu]);

  // Lade Kategorien und Altersgruppen (mit Cache)
  const loadCategoriesAndAgeGroups = async () => {
    try {
      // Prüfe Cache zuerst
      const cachedCategories = cacheService.get('categories');
      const cachedAgeGroups = cacheService.get('ageGroups');

      if (cachedCategories && cachedAgeGroups) {
        setCategories(cachedCategories);
        setAgeGroups(cachedAgeGroups);
        return; // Verwende Cache, kein Request nötig
      }

      // Cache nicht vorhanden oder abgelaufen, lade vom Backend
      const [categoriesResult, ageGroupsResult] = await Promise.all([
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (categoriesResult.success) {
        setCategories(categoriesResult.data);
        cacheService.set('categories', categoriesResult.data);
      } else {
        console.error('Fehler beim Laden der Kategorien:', categoriesResult.error);
      }

      if (ageGroupsResult.success) {
        setAgeGroups(ageGroupsResult.data);
        cacheService.set('ageGroups', ageGroupsResult.data);
      } else {
        console.error('Fehler beim Laden der Altersgruppen:', ageGroupsResult.error);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien und Altersgruppen:', error);
    }
  };

  // Prüfe ob bereits Events existieren
  const checkExistingEvents = async () => {
    try {
      const result = await scheduleService.hasExistingEvents();
      if (result.success) {
        setHasExistingEvents(result.data.hasEvents);
      }
    } catch (error) {
      console.error('Fehler beim Prüfen der Events:', error);
    }
  };

  // Hilfsfunktion: Erstelle Mittagspause-Events für einen Tag
  const createLunchBreakEvents = (events, settings) => {
    if (!settings || !settings.lunch_start_time || !settings.lunch_duration_minutes) {
      return [];
    }

    const lunchStartTime = settings.lunch_start_time.value || '12:30';
    const lunchDurationMinutes = parseInt(settings.lunch_duration_minutes.value || '60', 10);

    // Gruppiere Events nach Tag
    const eventsByDay = {};
    events.forEach(event => {
      if (event.startTime) {
        const eventDate = new Date(event.startTime);
        const dayKey = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD
        if (!eventsByDay[dayKey]) {
          eventsByDay[dayKey] = [];
        }
        eventsByDay[dayKey].push(event);
      }
    });

    // Erstelle Mittagspause-Events für jeden Tag
    const lunchEvents = [];
    Object.keys(eventsByDay).forEach(dayKey => {
      const [hours, minutes] = lunchStartTime.split(':').map(Number);
      const lunchStart = new Date(dayKey);
      lunchStart.setHours(hours, minutes, 0, 0);
      const lunchEnd = new Date(lunchStart);
      lunchEnd.setMinutes(lunchEnd.getMinutes() + lunchDurationMinutes);

      // Erstelle Mittagspause-Event für beide Flächen
      ['Fläche A', 'Fläche B'].forEach(venue => {
        lunchEvents.push({
          id: `lunch-${dayKey}-${venue}`,
          venue: venue,
          startTime: lunchStart.toISOString(),
          endTime: lunchEnd.toISOString(),
          status: 'Ausstehend',
          isLunchBreak: true,
          group: null
        });
      });
    });

    return lunchEvents;
  };

  // Lade Zeitplan-Daten
  const loadScheduleData = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await scheduleService.getAllScheduleEventsWithFallback();

      if (result.success) {
        setScheduleData(result.data);

        // Lade Settings für Mittagspause
        let lunchEvents = [];
        try {
          const settingsResult = await settingsService.getAllSettings();
          if (settingsResult.success && settingsResult.data) {
            lunchEvents = createLunchBreakEvents(result.data, settingsResult.data);
          }
        } catch (error) {
          console.debug('Fehler beim Laden der Settings für Mittagspause (nicht kritisch):', error);
        }

        // Kombiniere normale Events mit Mittagspause-Events und sortiere nach Zeit
        const allEvents = [...result.data, ...lunchEvents].sort((a, b) => {
          return new Date(a.startTime) - new Date(b.startTime);
        });

        // Aktualisiere venuesData basierend auf allen Events
        const newVenuesData = {
          "Fläche A": allEvents.filter(item => item.venue === "Fläche A"),
          "Fläche B": allEvents.filter(item => item.venue === "Fläche B")
        };
        setVenuesData(newVenuesData);

        // Prüfe ob Team-Events vorhanden sind und lade Team-Daten
        const hasTeamEvent = result.data.some(event =>
          event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb'
        );

        if (hasTeamEvent) {
          try {
            const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
            if (teamGroupResponse.success && teamGroupResponse.data) {
              setTeamData(teamGroupResponse.data);
            }
          } catch (error) {
            console.debug('Fehler beim Laden der Team-Daten (nicht kritisch):', error);
          }
        }

        // Lade Partner-Teams für alle Partnerform-Gruppen
        const partnerFormGroups = result.data.filter(event =>
          event.group?.category?.type === 'Partnerform' && event.group?.id
        );

        if (partnerFormGroups.length > 0) {
          const partnerTeamsMap = new Map();
          await Promise.all(
            partnerFormGroups.map(async (event) => {
              try {
                const groupResponse = await groupsService.getGroupWithStartOrder(event.group.id);
                if (groupResponse.success && groupResponse.data?.isPartnerForm && groupResponse.data?.partnerTeams) {
                  partnerTeamsMap.set(event.group.id, groupResponse.data);
                }
              } catch (error) {
                console.debug(`Fehler beim Laden der Partner-Teams für Gruppe ${event.group.id} (nicht kritisch):`, error);
              }
            })
          );
          setPartnerTeamsData(partnerTeamsMap);
        }
      } else {
        setError(result.error);
        console.error('Fehler beim Laden des Zeitplans:', result.error);
      }
    } catch (error) {
      setError('Fehler beim Laden des Zeitplans');
      console.error('Fehler beim Laden des Zeitplans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lade Teilnehmer-Daten
  const loadParticipants = async () => {
    try {
      const result = await participantsService.getAllParticipants();
      if (result.success) {
        setParticipants(result.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Teilnehmer:', error);
    }
  };

  // Teilnehmer für ein Event finden
  const getParticipantsForEvent = (event) => {
    if (!event.group) return [];

    // Spezielle Behandlung für Partner-Teams
    const isPartnerForm = event.group?.category?.type === 'Partnerform';
    const partnerTeamsForGroup = partnerTeamsData.get(event.group?.id);

    if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
      // Sammle alle Teilnehmer aus allen Partner-Teams
      const allPartnerParticipants = [];
      partnerTeamsForGroup.partnerTeams.forEach(team => {
        if (team.participant1) allPartnerParticipants.push(team.participant1);
        if (team.participant2) allPartnerParticipants.push(team.participant2);
      });
      return allPartnerParticipants;
    }

    // Spezielle Behandlung für Team-Events (Gruppenwettbewerb)
    const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');

    if (isTeamEvent && teamData && teamData.teams) {
      // Sammle alle Teilnehmer aus allen Teams
      const allTeamParticipants = [];
      teamData.teams.forEach(team => {
        if (team.participants && Array.isArray(team.participants)) {
          allTeamParticipants.push(...team.participants);
        }
      });
      return allTeamParticipants;
    }

    // Normale Gruppen: Filtere über ParticipantGroup
    if (!participants.length) return [];

    const eventParticipants = participants.filter(participant => {
      if (!participant.groups || !Array.isArray(participant.groups)) {
        return false;
      }
      return participant.groups.some(group => group.id === event.group.id);
    });

    // Sortiere nach Startnummer
    eventParticipants.sort((a, b) => {
      const aGroup = a.groups.find(g => g.id === event.group.id);
      const bGroup = b.groups.find(g => g.id === event.group.id);
      const aStartNumber = aGroup ? aGroup.startNumber : 999;
      const bStartNumber = bGroup ? bGroup.startNumber : 999;
      return aStartNumber - bStartNumber;
    });

    return eventParticipants;
  };

  // Event Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDayChange = (day) => {
    setSelectedDay(day);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];
    if (selectedDay) {
      const dayOption = dayOptions.find(opt => opt.value === selectedDay);
      active.push({
        type: "Tag", value: dayOption?.label || selectedDay, onRemove: () => {
          // Setze ersten verfügbaren Tag wenn Filter entfernt wird
          const firstDay = dayOptions.find(opt => opt.value !== selectedDay) || dayOptions[0];
          if (firstDay) {
            setSelectedDay(firstDay.value);
          }
        }
      });
    }
    if (selectedCategory !== "all") {
      active.push({ type: "Kategorie", value: selectedCategory, onRemove: () => setSelectedCategory("all") });
    }
    if (selectedStatus !== "all") {
      active.push({ type: "Status", value: selectedStatus, onRemove: () => setSelectedStatus("all") });
    }
    if (selectedGender !== "all") {
      active.push({ type: "Geschlecht", value: selectedGender === "männlich" ? "Männlich" : selectedGender === "weiblich" ? "Weiblich" : "Gemischt", onRemove: () => setSelectedGender("all") });
    }
    if (selectedAgeGroup !== "all") {
      active.push({ type: "Altersgruppe", value: selectedAgeGroup, onRemove: () => setSelectedAgeGroup("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  // Reset all filters
  const handleResetFilters = () => {
    // Setze ersten verfügbaren Tag
    if (dayOptions.length > 0) {
      setSelectedDay(dayOptions[0].value);
    }
    setSelectedCategory("all");
    setSelectedStatus("all");
    setSelectedGender("all");
    setSelectedAgeGroup("all");
    setSearchTerm("");
    setHideCompleted(false);
  };

  const handleToggleExpand = async (eventId) => {
    const newExpandedEvents = new Set(expandedEvents);
    const event = scheduleData.find(e => e.id === eventId);
    const isTeamEvent = !!(event?.group?.isTeamCompetition || event?.group?.id === 'team-competition' || event?.group?.name === 'Gruppenwettbewerb');

    if (newExpandedEvents.has(eventId)) {
      newExpandedEvents.delete(eventId);
      // Lösche nur erweiterte Nationen, aber behalte teamData
      // Team-Daten bleiben erhalten, damit Teilnehmeranzahl korrekt angezeigt wird
      setExpandedNations(new Set());
    } else {
      newExpandedEvents.add(eventId);
      // Lade Team-Daten wenn Team-Event erweitert wird (nur wenn noch nicht vorhanden)
      if (isTeamEvent && !teamData) {
        try {
          const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
          if (teamGroupResponse.success && teamGroupResponse.data) {
            setTeamData(teamGroupResponse.data);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Team-Daten:', error);
        }
      }
    }
    setExpandedEvents(newExpandedEvents);
  };

  const handleToggleNationExpansion = (nationId) => {
    const newExpandedNations = new Set(expandedNations);
    if (newExpandedNations.has(nationId)) {
      newExpandedNations.delete(nationId);
    } else {
      newExpandedNations.add(nationId);
    }
    setExpandedNations(newExpandedNations);
  };

  const handleExpandAll = () => {
    const allEventIds = filteredSchedule.map(event => event.id);
    setExpandedEvents(new Set(allEventIds));
  };

  const handleCollapseAll = () => {
    setExpandedEvents(new Set());
  };

  const handleToggleEditMode = () => {
    if (isEditMode && hasUnsavedChanges) {
      // Zeige Bestätigungsdialog bevor Änderungen verworfen werden
      setShowCancelEditConfirmation(true);
      return;
    }
    const newEditMode = !isEditMode;
    setIsEditMode(newEditMode);
    if (isEditMode) {
      loadScheduleData();
      setExpandedEvents(new Set());
      setHasUnsavedChanges(false);
      setChangeHistory([]);
    }
  };

  const handleConfirmCancelEdit = () => {
    setShowCancelEditConfirmation(false);
    setIsEditMode(false);
    loadScheduleData();
    setExpandedEvents(new Set());
    setHasUnsavedChanges(false);
    setChangeHistory([]);
    addToast('info', 'Änderungen wurden verworfen');
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      // Filtere Mittagspause-Events und Events ohne gültige ID aus venuesData heraus
      const filteredVenuesData = {};
      Object.keys(venuesData).forEach(venue => {
        filteredVenuesData[venue] = venuesData[venue].filter(event => {
          // Nur Events mit gültiger numerischer ID und keine Mittagspause-Events
          // Events müssen eine echte Datenbank-ID haben (numerisch, nicht String wie "lunch-...")
          return event.id &&
            typeof event.id === 'number' &&
            !event.isLunchBreak;
        });
      });

      const changes = {
        events: scheduleData.filter(event =>
          event.id &&
          typeof event.id === 'number' &&
          !event.isLunchBreak
        ),
        venuesData: filteredVenuesData,
        changeHistory: changeHistory,
        timestamp: new Date().toISOString()
      };

      const result = await scheduleService.saveScheduleChanges(changes);

      if (result.success) {
        addToast('success', result.message || 'Änderungen erfolgreich gespeichert');
        await loadScheduleData();
        setIsEditMode(false);
        setExpandedEvents(new Set());
        setHasUnsavedChanges(false);
        setChangeHistory([]);

        // Reset drag state
        setDraggedEvent(null);
        setDraggedFromVenue(null);
        setDraggedIndex(null);
        setDragOverIndex(null);
        setDragOverVenue(null);
      } else {
        addToast('error', result.error || 'Fehler beim Speichern der Änderungen');
      }
    } catch (error) {
      addToast('error', 'Fehler beim Speichern der Änderungen');
    } finally {
      setIsSaving(false);
    }
  };

  // Prüfe ob Event verschiebbar ist
  const isEventDraggable = (event) => {
    if (!event || event.isLunchBreak) return false;
    const nonDraggableStatuses = ['Aktiv', 'Vorbereitung', 'Abgeschlossen'];
    return !nonDraggableStatuses.includes(event.status);
  };

  // Drag & Drop Funktionalität für Events zwischen Venues
  const handleScheduleEventDragStart = (e, event, venue, index) => {
    // Prüfe ob Event verschiebbar ist
    if (!isEventDraggable(event)) {
      e.preventDefault();
      return;
    }

    let actualIndex = index;
    if (isEditMode) {
      const originalEvents = venuesData[venue];
      actualIndex = originalEvents.findIndex(e => e.id === event.id);
    }

    setDraggedEvent(event);
    setDraggedFromVenue(venue);
    setDraggedIndex(actualIndex);
    e.dataTransfer.effectAllowed = "move";

    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 0, 0);
    }
  };

  const handleScheduleEventDragOver = (e, venue, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverVenue(venue);
    setDragOverIndex(index);
  };

  const handleScheduleEventDragLeave = () => {
    setDragOverIndex(null);
    setDragOverVenue(null);
  };

  const handleScheduleEventDrop = async (e, targetVenue, targetIndex) => {
    e.preventDefault();

    if (!draggedEvent || draggedFromVenue === null || draggedIndex === null) {
      return;
    }

    try {
      const newVenuesData = { ...venuesData };
      const sourceVenue = newVenuesData[draggedFromVenue];
      const targetVenueEvents = newVenuesData[targetVenue];

      // Entferne aus Quelle
      const [movedEvent] = sourceVenue.splice(draggedIndex, 1);
      movedEvent.venue = targetVenue;

      // Füge zu Ziel hinzu
      targetVenueEvents.splice(targetIndex, 0, movedEvent);

      // Aktualisiere sequenceNumber für alle Events in beiden Venues
      const updateSequenceNumbers = (venueEvents) => {
        venueEvents.forEach((event, index) => {
          event.sequenceNumber = index + 1;
        });
      };

      updateSequenceNumbers(newVenuesData[draggedFromVenue]);
      updateSequenceNumbers(newVenuesData[targetVenue]);

      // Aktualisiere lokalen State sofort
      setVenuesData(newVenuesData);

      // Aktualisiere auch scheduleData für Konsistenz
      const newScheduleData = scheduleData.map(event => {
        if (event.id === draggedEvent.id) {
          return { ...event, venue: targetVenue, sequenceNumber: targetIndex + 1 };
        }
        const venueEvents = newVenuesData[event.venue];
        if (venueEvents) {
          const eventInVenue = venueEvents.find(e => e.id === event.id);
          if (eventInVenue) {
            return { ...event, sequenceNumber: eventInVenue.sequenceNumber };
          }
        }
        return event;
      });
      setScheduleData(newScheduleData);

      // Markiere als ungespeicherte Änderung
      setHasUnsavedChanges(true);
      setChangeHistory(prev => [...prev, {
        type: 'event_moved',
        eventId: draggedEvent.id,
        fromVenue: draggedFromVenue,
        toVenue: targetVenue,
        fromIndex: draggedIndex,
        toIndex: targetIndex,
        timestamp: new Date().toISOString()
      }]);

      // Kein sofortiger API-Call — Änderungen werden erst beim expliziten Speichern persistiert

    } catch (error) {
      console.error('Fehler beim Verschieben:', error);
    }

    // Reset drag state
    setDraggedEvent(null);
    setDraggedFromVenue(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverVenue(null);
  };

  const handleScheduleEventDragEnd = () => {
    setDraggedEvent(null);
    setDraggedFromVenue(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverVenue(null);
  };

  // Context Menu Handler
  const handleContextMenu = (e, event, venue, index) => {
    e.preventDefault();
    // Nur im Bearbeitungsmodus und für verschiebbare Events
    if (!isEditMode || !isEventDraggable(event)) {
      return;
    }
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      event,
      venue,
      index
    });
    setContextMenuSubmenu(null);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setContextMenuSubmenu(null);
    setContextMenuDaySubmenu(null);
  };

  // Quick Actions Handler
  const handleMoveEvent = async (action, targetVenue = null, targetDay = null) => {
    if (!contextMenu) return;

    const { event, venue, index } = contextMenu;
    const newVenuesData = { ...venuesData };

    try {
      let targetIndex = null;
      let finalVenue = targetVenue || venue;
      let finalDay = targetDay || selectedDay;

      // Finde aktuelle Position des Events im aktuellen Tag
      const currentDayEvents = newVenuesData[venue].filter(e =>
        e.startTime?.startsWith(selectedDay) && !e.isLunchBreak
      );
      const currentIndexInDay = currentDayEvents.findIndex(e => e.id === event.id);

      // Finde Ziel-Index basierend auf Aktion
      if (action === 'same-index') {
        // Gleicher Index: Finde Position im Ziel
        const targetDayEvents = newVenuesData[finalVenue].filter(e =>
          e.startTime?.startsWith(finalDay) && !e.isLunchBreak
        );
        targetIndex = Math.min(currentIndexInDay, targetDayEvents.length);
      } else if (action === 'after-lunch') {
        // Nach Mittagspause: Finde Mittagspause und setze danach
        const targetDayAllEvents = newVenuesData[finalVenue].filter(e =>
          e.startTime?.startsWith(finalDay)
        );
        const lunchIndex = targetDayAllEvents.findIndex(e => e.isLunchBreak);
        if (lunchIndex >= 0) {
          // Finde erste Position nach Mittagspause (nur non-lunch events)
          const eventsAfterLunch = targetDayAllEvents.slice(lunchIndex + 1).filter(e => !e.isLunchBreak);
          targetIndex = lunchIndex + 1 - (targetDayAllEvents.length - eventsAfterLunch.length - 1);
        } else {
          const targetDayEvents = newVenuesData[finalVenue].filter(e =>
            e.startTime?.startsWith(finalDay) && !e.isLunchBreak
          );
          targetIndex = targetDayEvents.length;
        }
      } else if (action === 'top') {
        targetIndex = 0;
      } else if (action === 'bottom') {
        const targetDayEvents = newVenuesData[finalVenue].filter(e =>
          e.startTime?.startsWith(finalDay) && !e.isLunchBreak
        );
        targetIndex = targetDayEvents.length;
      } else if (action === 'up') {
        // Innerhalb der Fläche nach oben (nur wenn nicht schon am Anfang)
        if (currentIndexInDay > 0) {
          targetIndex = currentIndexInDay - 1;
        } else {
          handleCloseContextMenu();
          return;
        }
        finalDay = selectedDay; // Bleibt am selben Tag
        finalVenue = venue; // Bleibt in derselben Fläche
      } else if (action === 'down') {
        // Innerhalb der Fläche nach unten (nur wenn nicht schon am Ende)
        const currentDayEventsCount = currentDayEvents.length;
        if (currentIndexInDay < currentDayEventsCount - 1) {
          targetIndex = currentIndexInDay + 1;
        } else {
          handleCloseContextMenu();
          return;
        }
        finalDay = selectedDay; // Bleibt am selben Tag
        finalVenue = venue; // Bleibt in derselben Fläche
      }

      if (targetIndex === null) {
        handleCloseContextMenu();
        return;
      }

      // Entferne Event von alter Position
      const sourceVenueEvents = [...newVenuesData[venue]];
      const eventIndex = sourceVenueEvents.findIndex(e => e.id === event.id);
      if (eventIndex < 0) {
        handleCloseContextMenu();
        return;
      }

      const [movedEvent] = sourceVenueEvents.splice(eventIndex, 1);

      // Aktualisiere Event-Daten wenn nötig
      if (targetVenue && targetVenue !== venue) {
        movedEvent.venue = targetVenue;
      }
      if (targetDay && targetDay !== selectedDay) {
        // Aktualisiere startTime und endTime für neuen Tag
        const oldDate = new Date(movedEvent.startTime);
        const newDate = new Date(targetDay);
        const timeDiff = oldDate.getTime() - new Date(oldDate.toISOString().split('T')[0]).getTime();
        newDate.setTime(newDate.getTime() + timeDiff);
        movedEvent.startTime = newDate.toISOString();

        if (movedEvent.endTime) {
          const oldEndDate = new Date(movedEvent.endTime);
          const duration = oldEndDate.getTime() - oldDate.getTime();
          const newEndDate = new Date(newDate);
          newEndDate.setTime(newEndDate.getTime() + duration);
          movedEvent.endTime = newEndDate.toISOString();
        }
      }

      // Füge Event an neuer Position ein
      const targetVenueEvents = [...newVenuesData[finalVenue]];
      const targetDayAllEvents = targetVenueEvents.filter(e =>
        e.startTime?.startsWith(finalDay)
      );
      const targetDayNonLunchEvents = targetDayAllEvents.filter(e => !e.isLunchBreak);

      // Finde Einfügeposition
      let insertPosition = 0;
      if (action === 'after-lunch') {
        // Finde Position nach Mittagspause
        const lunchIndex = targetDayAllEvents.findIndex(e => e.isLunchBreak);
        if (lunchIndex >= 0) {
          // Finde Index in der gesamten Liste nach Mittagspause
          const lunchEvent = targetDayAllEvents[lunchIndex];
          const lunchIndexInFull = targetVenueEvents.findIndex(e => e.id === lunchEvent.id);
          insertPosition = lunchIndexInFull + 1;
        } else {
          // Keine Mittagspause, füge am Ende des Tages ein
          const lastEventOfDay = targetDayAllEvents[targetDayAllEvents.length - 1];
          const lastIndexInFull = targetVenueEvents.findIndex(e => e.id === lastEventOfDay.id);
          insertPosition = lastIndexInFull + 1;
        }
      } else {
        // Finde Position basierend auf targetIndex
        if (targetIndex === 0) {
          // Am Anfang des Tages
          const firstEventOfDay = targetDayAllEvents[0];
          insertPosition = targetVenueEvents.findIndex(e => e.id === firstEventOfDay.id);
        } else if (targetIndex >= targetDayNonLunchEvents.length) {
          // Am Ende des Tages
          const lastEventOfDay = targetDayAllEvents[targetDayAllEvents.length - 1];
          const lastIndexInFull = targetVenueEvents.findIndex(e => e.id === lastEventOfDay.id);
          insertPosition = lastIndexInFull + 1;
        } else {
          // An spezifischer Position
          const targetEvent = targetDayNonLunchEvents[targetIndex];
          insertPosition = targetVenueEvents.findIndex(e => e.id === targetEvent.id);
        }
      }

      // Füge Event ein
      targetVenueEvents.splice(insertPosition, 0, movedEvent);

      // Aktualisiere venuesData
      newVenuesData[venue] = sourceVenueEvents;
      newVenuesData[finalVenue] = targetVenueEvents;

      // Aktualisiere sequenceNumber für alle Events in beiden Venues
      const updateSequenceNumbers = (venueEvents) => {
        venueEvents.forEach((event, idx) => {
          if (!event.isLunchBreak) {
            event.sequenceNumber = idx + 1;
          }
        });
      };

      updateSequenceNumbers(newVenuesData[venue]);
      updateSequenceNumbers(newVenuesData[finalVenue]);

      setVenuesData(newVenuesData);

      // Aktualisiere scheduleData
      const newScheduleData = scheduleData.map(e => {
        if (e.id === event.id) {
          return { ...movedEvent };
        }
        return e;
      });
      setScheduleData(newScheduleData);

      // Markiere als ungespeicherte Änderung
      setHasUnsavedChanges(true);
      setChangeHistory(prev => [...prev, {
        type: 'event_moved',
        eventId: event.id,
        fromVenue: venue,
        toVenue: finalVenue,
        fromIndex: index,
        toIndex: targetIndex,
        action,
        timestamp: new Date().toISOString()
      }]);

      // Kein sofortiger API-Call — Änderungen werden erst beim expliziten Speichern persistiert
      addToast('success', 'Event verschoben (noch nicht gespeichert)');
    } catch (error) {
      console.error('Fehler beim Verschieben:', error);
      addToast('error', 'Fehler beim Verschieben des Events');
    }

    handleCloseContextMenu();
  };

  // Generiere dayOptions dynamisch basierend auf den vorhandenen Events (ohne "Alle Tage")
  const dayOptions = useMemo(() => {
    const days = new Set();

    scheduleData.forEach(event => {
      if (event.startTime) {
        const day = event.startTime.split('T')[0];
        days.add(day);
      }
    });

    const sortedDays = Array.from(days).sort();

    return sortedDays.map(day => {
      const date = new Date(day);
      const formattedDate = date.toLocaleDateString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      return {
        value: day,
        label: formattedDate
      };
    });
  }, [scheduleData]);

  // Setze ersten Tag als Standard, wenn noch keiner ausgewählt
  useEffect(() => {
    if (!selectedDay && dayOptions.length > 1) {
      // Überspringe "all" Option und wähle ersten echten Tag
      const firstRealDay = dayOptions.find(opt => opt.value !== "all");
      if (firstRealDay) {
        setSelectedDay(firstRealDay.value);
      }
    }
  }, [dayOptions, selectedDay]);

  const categoryOptions = [
    { value: "all", label: t("admin.schedule.allCategories", "Alle Kategorien") },
    ...categories.map((category) => ({
      value: category.name,
      label: category.name
    }))
  ];

  const ageGroupOptions = [
    { value: "all", label: "Alle Altersgruppen" },
    ...ageGroups.map((group) => ({
      value: group.name,
      label: group.name
    }))
  ];

  const statusOptions = [
    { value: "all", label: t("admin.schedule.allStatuses", "Alle Status") },
    { value: "Ausstehend", label: t("admin.schedule.pending", "Ausstehend") },
    { value: "Aktiv", label: t("admin.schedule.active", "Aktiv") },
    { value: "Vorbereitung", label: t("admin.groups.preparation", "Vorbereitung") },
    { value: "Abgeschlossen", label: t("admin.schedule.completed", "Abgeschlossen") }
  ];

  const genderOptions = [
    { value: "all", label: t("admin.schedule.allGenders", "Alle Geschlechter") },
    { value: "männlich", label: "Männlich" },
    { value: "weiblich", label: "Weiblich" },
    { value: "gemischt", label: "Gemischt" }
  ];

  const filteredSchedule = scheduleData.filter((item) => {
    // Hole Teilnehmer für dieses Event für die Suche
    const eventParticipants = getParticipantsForEvent(item);
    const matchesParticipants = eventParticipants.some(p =>
      (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.firstname && p.firstname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.lastname && p.lastname.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const matchesSearch = (item.group?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.venue || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      matchesParticipants;
    const matchesDay = !selectedDay || item.startTime?.startsWith(selectedDay);
    const matchesCategory = selectedCategory === "all" || item.group?.category?.name === selectedCategory;
    const matchesAgeGroup = selectedAgeGroup === "all" || item.group?.ageGroup?.name === selectedAgeGroup;
    const matchesStatus = selectedStatus === "all" || item.status === selectedStatus;
    const matchesGender = selectedGender === "all" || item.group?.gender === selectedGender;
    // Wenn "Abgeschlossen ausblenden" aktiviert ist und Status-Filter nicht auf "Abgeschlossen" steht, verstecke abgeschlossene Events
    const matchesHideCompleted = !hideCompleted || selectedStatus === "Abgeschlossen" || item.status !== "Abgeschlossen";
    return matchesSearch && matchesDay && matchesCategory && matchesAgeGroup && matchesStatus && matchesGender && matchesHideCompleted;
  });

  // Berechne die tatsächliche Dauer für eine Gruppe
  const calculateGroupDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '0min';

    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  };

  // Berechne die tatsächliche Dauer in Minuten für eine Gruppe
  const calculateGroupDurationMinutes = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;

    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    return Math.floor(durationMs / (1000 * 60));
  };

  // Berechne Gesamtanzahl der Athleten pro Fläche
  const getTotalParticipants = (events) => {
    return events.reduce((total, event) => total + getParticipantsForEvent(event).length, 0);
  };

  // Berechne reine Wettkampfzeit basierend auf der Summe aller Event-Dauern
  const calculateTimeEstimate = (events) => {
    if (events.length === 0) return '0min';

    let totalMinutes = 0;

    events.forEach(event => {
      if (event.startTime && event.endTime) {
        const start = new Date(event.startTime);
        const end = new Date(event.endTime);
        const durationMs = end.getTime() - start.getTime();
        const durationMinutes = Math.floor(durationMs / (1000 * 60));
        totalMinutes += durationMinutes;
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  };

  // Timeline-spezifische Hilfsfunktionen
  const getTimelineData = () => {
    const allEvents = [];

    Object.entries(displayVenuesData).forEach(([venue, events]) => {
      events.forEach(event => {
        allEvents.push({
          ...event,
          venue,
          startTime: new Date(event.startTime),
          endTime: new Date(event.endTime),
          duration: calculateGroupDurationMinutes(event.startTime, event.endTime)
        });
      });
    });

    // Sortiere nach Startzeit
    allEvents.sort((a, b) => a.startTime - b.startTime);

    // Finde den frühesten und spätesten Zeitpunkt
    const earliestTime = allEvents.length > 0 ? Math.min(...allEvents.map(e => e.startTime.getTime())) : new Date().getTime();
    const latestTime = allEvents.length > 0 ? Math.max(...allEvents.map(e => e.endTime.getTime())) : new Date().getTime();

    // Timeline-Höhe: Basis-Pixel/Minute; bei sehr kurzen Slots Gesamthöhe begrenzt anheben (ohne extremen Scroll bei langem Tag)
    const totalDurationMinutes = (latestTime - earliestTime) / (1000 * 60);
    const totalMs = latestTime - earliestTime;
    const minHeightPerMinute = 17;
    const MIN_EVENT_BLOCK_PX = 76; // Lesbare Karte: Titel + Meta + Status
    const MAX_TIMELINE_BOOST_FACTOR = 4; // max. Faktor über Basis-Höhe (kurzes Event in langem Tag)

    const baseTimeline = Math.max(totalDurationMinutes * minHeightPerMinute, 360);
    let timelineHeight = baseTimeline;

    if (totalMs > 0 && allEvents.length > 0) {
      const requiredHeights = allEvents.map((e) => {
        const dur = e.endTime.getTime() - e.startTime.getTime();
        if (dur <= 0) return 0;
        return (MIN_EVENT_BLOCK_PX * totalMs) / dur;
      });
      const requiredFromShortestSlot = Math.max(0, ...requiredHeights);
      const cappedBoost = Math.min(requiredFromShortestSlot, baseTimeline * MAX_TIMELINE_BOOST_FACTOR);
      timelineHeight = Math.max(baseTimeline, cappedBoost);
      timelineHeight = Math.min(timelineHeight, 16000); // harte Obergrenze (Scroll), Extremfälle
    }

    return {
      events: allEvents,
      earliestTime: new Date(earliestTime),
      latestTime: new Date(latestTime),
      totalDuration: latestTime - earliestTime,
      timelineHeight
    };
  };

  /** Top/Höhe in px — konsistent mit timelineHeight, keine minHeight-Überlappungen */
  const getEventTimelineLayout = (event, timelineData) => {
    const startTime = new Date(event.startTime);
    const endTime = new Date(event.endTime);
    const startOffset = startTime.getTime() - timelineData.earliestTime.getTime();
    const durationMs = endTime.getTime() - startTime.getTime();
    const { totalDuration, timelineHeight } = timelineData;
    if (!totalDuration || totalDuration <= 0) {
      return { topPx: 0, heightPx: 20, durationMinutes: durationMs / (1000 * 60) };
    }
    const topPx = (startOffset / totalDuration) * timelineHeight;
    const rawHeightPx = (durationMs / totalDuration) * timelineHeight;
    const heightPx = Math.max(rawHeightPx, 8);
    return {
      topPx,
      heightPx,
      durationMinutes: durationMs / (1000 * 60)
    };
  };

  const timelineStatusBorderL = (status) => {
    switch (status) {
      case 'Aktiv':
        return 'border-l-emerald-500 dark:border-l-emerald-400';
      case 'Vorbereitung':
        return 'border-l-orange-400 dark:border-l-orange-500';
      case 'Ausstehend':
        return 'border-l-yellow-400 dark:border-l-yellow-500';
      default:
        return 'border-l-slate-300 dark:border-l-slate-600';
    }
  };

  const formatTimeForTimeline = (date) => {
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Erstelle gefilterte Version von venuesData für die Anzeige
  const filteredVenuesData = useMemo(() => {
    const filtered = {
      "Fläche A": [],
      "Fläche B": []
    };

    Object.entries(venuesData).forEach(([venue, events]) => {
      filtered[venue] = events.filter(event => {
        // Tag-Filterung (muss ausgewählt sein)
        const matchesDay = !selectedDay || event.startTime?.startsWith(selectedDay);

        // Suche nach Event-Name, Ort oder Teilnehmern
        const eventParticipants = getParticipantsForEvent(event);
        const matchesParticipants = eventParticipants.some(p =>
          (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (p.firstname && p.firstname.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (p.lastname && p.lastname.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const matchesSearch = (event.group?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (event.venue || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          matchesParticipants;

        const matchesCategory = selectedCategory === "all" || event.group?.category?.name === selectedCategory;
        const matchesAgeGroup = selectedAgeGroup === "all" || event.group?.ageGroup?.name === selectedAgeGroup;
        const matchesStatus = selectedStatus === "all" || event.status === selectedStatus;
        const matchesGender = selectedGender === "all" || event.group?.gender === selectedGender;
        // Wenn "Abgeschlossen ausblenden" aktiviert ist und Status-Filter nicht auf "Abgeschlossen" steht, verstecke abgeschlossene Events
        const matchesHideCompleted = !hideCompleted || selectedStatus === "Abgeschlossen" || event.status !== "Abgeschlossen";
        return matchesDay && matchesSearch && matchesCategory && matchesAgeGroup && matchesStatus && matchesGender && matchesHideCompleted;
      });
    });

    return filtered;
  }, [venuesData, searchTerm, selectedDay, selectedCategory, selectedStatus, selectedGender, selectedAgeGroup, hideCompleted, participants, teamData, partnerTeamsData]);

  // Verwende gefilterte Daten für die Anzeige, aber behalte ursprüngliche venuesData für Drag & Drop
  const displayVenuesData = isEditMode ? venuesData : filteredVenuesData;

  // Berechne feste Nummern pro Event (pro Fläche + Tag), basierend auf ungefilterten Daten
  // Diese Nummern ändern sich NICHT durch Filter
  const eventSequenceNumbers = useMemo(() => {
    const sequenceMap = {};
    Object.entries(venuesData).forEach(([venue, events]) => {
      // Gruppiere Events nach Tag
      const eventsByDay = {};
      events.forEach(event => {
        if (event.isLunchBreak) return;
        const day = event.startTime ? new Date(event.startTime).toDateString() : 'unknown';
        if (!eventsByDay[day]) eventsByDay[day] = [];
        eventsByDay[day].push(event);
      });
      // Nummeriere Events pro Tag
      Object.values(eventsByDay).forEach(dayEvents => {
        dayEvents.forEach((event, idx) => {
          sequenceMap[event.id] = idx + 1;
        });
      });
    });
    return sequenceMap;
  }, [venuesData]);

  const getStatusLabel = (status) => {
    switch (status) {
      case "Ausstehend":
        return t("admin.schedule.pending", "Ausstehend");
      case "Aktiv":
        return t("admin.schedule.active", "Aktiv");
      case "Abgeschlossen":
        return t("admin.schedule.completed", "Abgeschlossen");
      case "Vorbereitung":
        return t("admin.schedule.preparation", "Vorbereitung");
      default:
        return status;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatiere das Datum für die Anzeige
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('de-DE', options);
  };

  // Funktion zum Gruppieren von Teilnehmern nach Nationen für Gruppenperformances
  const groupParticipantsByNation = (participants, event) => {
    const grouped = {};
    participants.forEach(participant => {
      const nationName = participant.nation?.name || 'Unbekannt';
      if (!grouped[nationName]) {
        grouped[nationName] = [];
      }
      grouped[nationName].push(participant);
    });

    Object.keys(grouped).forEach(nation => {
      grouped[nation].sort((a, b) => {
        const aGroup = a.groups.find(g => g.id === event?.group?.id);
        const bGroup = b.groups.find(g => g.id === event?.group?.id);
        const aStartNumber = aGroup ? aGroup.startNumber : 999;
        const bStartNumber = bGroup ? bGroup.startNumber : 999;
        return aStartNumber - bStartNumber;
      });
    });

    return grouped;
  };

  // Funktion zum Rendern der Teilnehmerliste
  const renderParticipantsList = (eventId, participants) => {
    const event = scheduleData.find(e => e.id === eventId);
    const isTeamEvent = !!(event?.group?.isTeamCompetition || event?.group?.id === 'team-competition' || event?.group?.name === 'Gruppenwettbewerb');
    const isGroupPerformance = event?.group?.category?.name === "Gruppenperformance";
    const isPartnerForm = event?.group?.category?.type === 'Partnerform';
    const partnerTeamsForGroup = partnerTeamsData.get(event?.group?.id);

    // Spezielle Darstellung für Partner-Teams
    if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
      return (
        <div className="grid grid-cols-2 gap-2">
          {partnerTeamsForGroup.partnerTeams.map((team) => (
            <div key={team.id} className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700/50 rounded-xl px-3 py-2.5">
              {/* Startnummer */}
              {team.startNumber && (
                <div className="flex justify-end mb-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-600 text-[10px] font-bold text-slate-600 dark:text-slate-300">
                    {team.startNumber}
                  </span>
                </div>
              )}
              {/* Athlet 1 */}
              <div className="flex items-center gap-1.5 min-w-0">
                {team.participant1?.nation?.code && (
                  <img
                    src={process.env.PUBLIC_URL + "/images/flags/" + team.participant1.nation.code + ".png"}
                    alt={team.participant1.nation.name}
                    className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                  />
                )}
                <span className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                  {team.participant1?.name || 'Unbekannt'}
                </span>
              </div>
              {/* Trennlinie */}
              <div className="flex items-center gap-1 my-1.5">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700/60"></div>
                <i className="fas fa-handshake text-[8px] text-slate-300 dark:text-slate-600"></i>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700/60"></div>
              </div>
              {/* Athlet 2 */}
              <div className="flex items-center gap-1.5 min-w-0">
                {team.participant2?.nation?.code && (
                  <img
                    src={process.env.PUBLIC_URL + "/images/flags/" + team.participant2.nation.code + ".png"}
                    alt={team.participant2.nation.name}
                    className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                  />
                )}
                <span className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                  {team.participant2?.name || 'Unbekannt'}
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Spezielle Darstellung für Team-Events (Gruppenwettbewerb)
    if (isTeamEvent && teamData && teamData.teams) {
      return (
        <div className="mt-4 space-y-4">
          <h5 className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
            Teams ({teamData.teams.length} Nationen, {teamData.currentParticipants || 0} Teilnehmer)
          </h5>
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {teamData.teams.map((team) => {
                const isExpanded = expandedNations.has(team.nationId);
                return (
                  <div key={team.nationId} className="transition-colors">
                    {/* Nationen-Header (ausklappbar) */}
                    <button
                      onClick={() => handleToggleNationExpansion(team.nationId)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          <i className="fas fa-chevron-right text-slate-400 dark:text-slate-500 text-sm"></i>
                        </div>
                        {team.startNumber && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary dark:bg-primary-light text-white">
                            #{team.startNumber}
                          </span>
                        )}
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + (team.nationCode || 'DE') + ".png"}
                          alt={team.nationName}
                          className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{team.nationName}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{team.teamSize} Teilnehmer</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-700/30 dark:border-blue-300/30">
                          <i className="fas fa-users text-[10px]"></i>
                          Team
                        </span>
                      </div>
                    </button>

                    {/* Ausklappbare Teilnehmer-Liste */}
                    {isExpanded && team.participants && (
                      <div className="bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                              <thead className="bg-slate-100 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">#</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Teilnehmer</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Alter</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Geschlecht</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                                {team.participants.map((participant, index) => (
                                  <tr key={participant.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50">
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                                      <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                                        {participant.participantNumber || index + 1}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">{participant.name}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{participant.age || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${participant.gender === 'männlich' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-700/30 dark:border-blue-300/30' :
                                        participant.gender === 'weiblich' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border-pink-700/30 dark:border-pink-300/30' :
                                          'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-600/30 dark:border-slate-400/30'
                                        }`}>
                                        {participant.gender === 'männlich' ? 'Männlich' : participant.gender === 'weiblich' ? 'Weiblich' : '-'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    if (isGroupPerformance) {
      const groupedParticipants = groupParticipantsByNation(participants, event);
      const currentNationOrder = Object.keys(groupedParticipants);

      return (
        <div className="grid grid-cols-2 gap-2">
          {currentNationOrder.map((nation) => {
            const teamParticipants = groupedParticipants[nation];
            return (
              <div key={nation} className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700/50 rounded-xl px-3 py-2.5">
                {/* Nation-Header */}
                <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200 dark:border-slate-700/60">
                  <img
                    src={process.env.PUBLIC_URL + "/images/flags/" + (teamParticipants[0]?.nation?.code || 'DE') + ".png"}
                    alt={nation}
                    className="w-5 h-5 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                  />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate flex-1">{nation}</span>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0">{teamParticipants.length}×</span>
                </div>
                {/* Athleten */}
                <div className="space-y-1">
                  {teamParticipants.map((participant) => (
                    <div key={participant.id} className="flex items-center gap-1.5 min-w-0">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 dark:bg-slate-600 text-[9px] font-bold text-slate-600 dark:text-slate-300 flex-shrink-0">
                        {participant.participantNumber}
                      </span>
                      <span className="text-xs text-slate-700 dark:text-slate-300 truncate">{participant.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      );
    } else {
      // Normale Teilnehmerliste für reguläre Events
      return (
        <div className="grid grid-cols-2 gap-2">
          {participants.map((participant) => (
            <div key={participant.id} className="flex items-center gap-2.5 bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700/50 rounded-xl px-3 py-2.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-600 text-[10px] font-bold text-slate-600 dark:text-slate-300 flex-shrink-0">
                {participant.participantNumber}
              </span>
              <img
                src={process.env.PUBLIC_URL + "/images/flags/" + (participant.nation?.code || 'DE') + ".png"}
                alt={participant.nation?.name}
                className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{participant.name}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 truncate">{participant.nation?.name}</div>
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  // Handler für Zeitplan-Erstellung/Neu-Erstellung
  const handleGenerateSchedule = () => {
    setConfirmationType('generate');
    setShowConfirmation(true);
  };

  // Handler für Einstellungen (Zeitplankonfiguration)
  const handleOpenSettings = () => {
    setShowMoreMenu(false);
    navigate('/admin/schedule-settings');
  };

  const handleConfirmGenerate = async () => {
    setIsGenerating(true);
    setShowConfirmation(false);

    try {
      let result;

      result = await scheduleService.generateSchedule();

      if (result.success) {
        addToast('success', result.message || 'Zeitplan erfolgreich erstellt');
        await loadScheduleData();
        await checkExistingEvents();
      } else {
        addToast('error', result.error || 'Fehler beim Erstellen des Zeitplans');
      }
    } catch (error) {
      addToast('error', 'Fehler beim Erstellen des Zeitplans');
      console.error('Fehler:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Toast Handler
  const addToast = (type, message) => setToast({ show: true, message, type });
  const hideToast = () => setToast(prev => ({ ...prev, show: false }));

  // PDF Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const filters = {
        day: selectedDay,
        category: selectedCategory,
        ageGroup: selectedAgeGroup,
        status: selectedStatus,
        gender: selectedGender,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportSchedulePDF(filters);

      if (result.success) {
        addToast('success', 'Zeitplan-PDF erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim PDF-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim PDF-Export');
      console.error("Fehler beim PDF-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Turnier starten
  const handleStartTournament = async () => {
    setIsStartingTournament(true);
    try {
      const result = await scheduleService.startTournament();

      if (result.success) {
        addToast('success', result.message);
        await loadScheduleData();
      } else {
        addToast('error', `Fehler beim Starten des Turniers: ${result.error}`);
      }
    } catch (error) {
      console.error('Fehler beim Starten des Turniers:', error);
      addToast('error', 'Fehler beim Starten des Turniers');
    } finally {
      setIsStartingTournament(false);
    }
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-6 animate-pulse">
            <div className="flex items-center gap-3 mb-1">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded-xl w-48"></div>
              <div className="h-6 w-10 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-xl w-80"></div>
          </div>
          {/* Controls Skeleton */}
          <div className="mb-5 flex items-center justify-between animate-pulse">
            <div className="h-9 w-36 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            <div className="flex gap-2">
              <div className="h-9 w-28 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>
          </div>
          {/* Filter Section Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6 animate-pulse">
            <div className="h-10 bg-slate-100 dark:bg-slate-700 rounded-xl mb-5"></div>
            <div className="mb-5">
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-8 mb-2"></div>
              <div className="flex gap-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-8 w-28 bg-slate-100 dark:bg-slate-700 rounded-xl"></div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {[...Array(4)].map((_, i) => (
                <div key={i}>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-1.5"></div>
                  <div className="h-10 bg-slate-100 dark:bg-slate-700 rounded-xl"></div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4">
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-14 mb-2.5"></div>
              <div className="flex gap-2">
                <div className="h-7 w-28 bg-slate-100 dark:bg-slate-700 rounded-xl"></div>
                <div className="h-7 w-28 bg-slate-100 dark:bg-slate-700 rounded-xl"></div>
              </div>
            </div>
          </div>
          {/* Venues Grid Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
                <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                    <div>
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-xl w-24 mb-2"></div>
                      <div className="flex gap-1.5">
                        <div className="h-5 w-16 bg-slate-100 dark:bg-slate-700/50 rounded-full"></div>
                        <div className="h-5 w-20 bg-slate-100 dark:bg-slate-700/50 rounded-full"></div>
                        <div className="h-5 w-14 bg-slate-100 dark:bg-slate-700/50 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="rounded-2xl border border-slate-100 dark:border-slate-700/60 p-4 border-l-4 border-l-slate-200 dark:border-l-slate-600">
                      <div className="flex justify-between mb-2">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-xl w-40"></div>
                        <div className="h-4 bg-slate-100 dark:bg-slate-700/50 rounded-xl w-20"></div>
                      </div>
                      <div className="h-3 bg-slate-100 dark:bg-slate-700/50 rounded w-56 mb-3"></div>
                      <div className="border-t border-slate-100 dark:border-slate-700/60 pt-2.5 flex justify-between">
                        <div className="h-5 w-20 bg-slate-100 dark:bg-slate-700/50 rounded-full"></div>
                        <div className="h-6 w-24 bg-slate-100 dark:bg-slate-700/50 rounded-xl"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
              </div>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1">{error}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">Der Zeitplan konnte nicht geladen werden.</p>
              <button
                onClick={loadScheduleData}
                className="inline-flex items-center gap-2 h-9 px-4 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl shadow-sm hover:bg-primary-dark dark:hover:bg-primary transition-colors"
              >
                <i className="fas fa-redo text-xs"></i>
                {t("admin.retry", "Erneut versuchen")}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                    {t("admin.schedule.title", "Zeitplan")}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    <i className="fas fa-calendar-alt text-[10px]"></i>
                    {filteredSchedule.length}
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t("admin.schedule.subtitle", "Verwalte alle Events und deren Zeitplan.")}
                </p>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
            {/* Left: View Mode Toggle */}
            <div className="inline-flex items-center rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 p-1">
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${viewMode === 'cards'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                  }`}
              >
                <i className="fas fa-th-large text-[11px]"></i>
                <span>Karten</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('timeline')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${viewMode === 'timeline'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                  }`}
              >
                <i className="fas fa-stream text-[11px]"></i>
                <span>Timeline</span>
              </button>
            </div>

            {/* Right: Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Generate Schedule Button - nur sichtbar wenn noch kein Zeitplan existiert */}
              {!hasExistingEvents && (
                <button
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-xl text-sm font-medium transition-colors bg-primary dark:bg-primary-light text-white shadow-sm hover:bg-primary-dark dark:hover:bg-primary disabled:opacity-50"
                  onClick={handleGenerateSchedule}
                  disabled={isGenerating}
                >
                  <i className={`fas ${isGenerating ? 'fa-spinner fa-spin' : 'fa-calendar-plus'} text-xs`}></i>
                  {isGenerating ? "Erstelle…" : "Zeitplan erstellen"}
                </button>
              )}

              {/* Edit Mode Buttons */}
              {isEditMode ? (
                <>
                  <button
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-xl text-sm font-medium transition-colors bg-primary dark:bg-primary-light text-white shadow-sm hover:bg-primary-dark dark:hover:bg-primary disabled:opacity-50"
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                    title="Änderungen speichern (Ctrl+S)"
                  >
                    <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'} text-xs`}></i>
                    {isSaving ? "Speichere…" : hasUnsavedChanges ? `Speichern (${changeHistory.length})` : "Speichern"}
                  </button>
                  <button
                    className="inline-flex items-center gap-2 h-9 px-3.5 rounded-xl text-sm font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                    onClick={handleToggleEditMode}
                    disabled={isSaving}
                    title="Bearbeitung abbrechen (Esc)"
                  >
                    <i className="fas fa-times text-xs"></i>
                    Abbrechen
                  </button>
                </>
              ) : (
                <button
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-xl text-sm font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                  onClick={handleToggleEditMode}
                  title="Zeitplan bearbeiten (E)"
                >
                  <i className="fas fa-edit text-xs"></i>
                  Bearbeiten
                </button>
              )}

              {/* More Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-sm font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                  title="Weitere Aktionen"
                >
                  <i className="fas fa-ellipsis-v text-xs"></i>
                </button>

                {showMoreMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl min-w-[200px] z-50 overflow-hidden">
                      <div className="py-1">
                        {hasExistingEvents && (
                          <button
                            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors"
                            onClick={() => { handleGenerateSchedule(); setShowMoreMenu(false); }}
                            disabled={isGenerating}
                          >
                            <i className={`fas ${isGenerating ? 'fa-spinner fa-spin' : 'fa-sync'} text-slate-400 dark:text-slate-500 text-xs w-4`}></i>
                            {isGenerating ? "Erstelle…" : "Zeitplan neu erstellen"}
                          </button>
                        )}
                        <button
                          className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors"
                          onClick={() => { handleExportPDF(); setShowMoreMenu(false); }}
                          disabled={isExporting}
                        >
                          <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-file-pdf'} text-slate-400 dark:text-slate-500 text-xs w-4`}></i>
                          {isExporting ? "Exportiere…" : "PDF exportieren"}
                        </button>
                        <div className="my-1 border-t border-slate-100 dark:border-slate-700/60"></div>
                        <button
                          className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors"
                          onClick={() => {
                            navigate('/admin/schedule/block-preview');
                            setShowMoreMenu(false);
                          }}
                        >
                          <i className="fas fa-cubes text-slate-400 dark:text-slate-500 text-xs w-4"></i>
                          Block-Vorschau
                        </button>
                        <button
                          className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors"
                          onClick={handleOpenSettings}
                        >
                          <i className="fas fa-sliders-h text-slate-400 dark:text-slate-500 text-xs w-4"></i>
                          Einstellungen
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Edit-Mode Banner */}
          {isEditMode && (
            <div className="mb-5 px-4 py-2.5 bg-primary/5 dark:bg-primary-light/10 border border-primary/20 dark:border-primary-light/20 rounded-xl flex items-center gap-2.5">
              <i className="fas fa-pencil-alt text-primary dark:text-primary-light text-xs flex-shrink-0"></i>
              <span className="text-sm text-primary dark:text-primary-light font-medium flex-1">
                Bearbeitungsmodus aktiv – Events per Drag & Drop oder Rechtsklick verschieben.
              </span>
              {hasUnsavedChanges && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                  <i className="fas fa-dot-circle text-[9px]"></i>
                  {changeHistory.length} ungespeichert
                </span>
              )}
            </div>
          )}

          {/* Filter Section */}
          <div id="filter-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
            {/* Search Bar */}
            <div className="mb-5">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("admin.schedule.search", "Event, Ort oder Teilnehmer suchen…")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
                />
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs"></i>
              </div>
            </div>

            {/* Day Filter - Chips */}
            <div className="mb-5">
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
                Tag
              </label>
              <div className="flex flex-wrap gap-2">
                {dayOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`inline-flex items-center gap-1.5 h-8 px-3.5 text-xs font-medium rounded-xl transition-colors ${selectedDay === option.value
                      ? 'bg-primary dark:bg-primary-light text-white shadow-sm'
                      : 'bg-white dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                      }`}
                    onClick={() => handleDayChange(option.value)}
                  >
                    <i className="fas fa-calendar text-[9px]"></i>
                    {option.label}
                  </button>
                ))}
              </div>
              {!selectedDay && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                  <i className="fas fa-exclamation-triangle text-[10px]"></i>
                  Bitte einen Tag auswählen
                </p>
              )}
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Kategorie
                </label>
                <GenericSelectTailwind
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  options={categoryOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                  placeholder="Alle Kategorien"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Altersgruppe
                </label>
                <GenericSelectTailwind
                  value={selectedAgeGroup}
                  onChange={handleAgeGroupChange}
                  options={ageGroupOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                  placeholder="Alle Altersgruppen"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Status
                </label>
                <GenericSelectTailwind
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  options={statusOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label,
                    icon:
                      opt.value === "Ausstehend" ? <i className="fas fa-clock text-amber-500" /> :
                        opt.value === "Aktiv" ? <i className="fas fa-play text-emerald-500" /> :
                          opt.value === "Vorbereitung" ? <i className="fas fa-hourglass-half text-orange-500" /> :
                            opt.value === "Abgeschlossen" ? <i className="fas fa-check text-slate-500" /> : null
                  }))}
                  leftIcon={<i className="fas fa-traffic-light text-slate-400 dark:text-slate-500" />}
                  placeholder="Alle Status"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Geschlecht
                </label>
                <GenericSelectTailwind
                  value={selectedGender}
                  onChange={handleGenderChange}
                  options={genderOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label,
                    icon:
                      opt.value === "männlich" ? <i className="fas fa-mars text-blue-600" /> :
                        opt.value === "weiblich" ? <i className="fas fa-venus text-pink-600" /> :
                          opt.value === "gemischt" ? <i className="fas fa-venus-mars text-purple-500" /> : null
                  }))}
                  leftIcon={<i className="fas fa-venus-mars text-slate-400 dark:text-slate-500" />}
                  placeholder="Alle Geschlechter"
                />
              </div>
            </div>

            {/* Active Filters */}
            {getActiveFilters().length > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4 mb-4">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    Aktive Filter
                  </span>
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                  >
                    <i className="fas fa-undo text-[10px]"></i>
                    Zurücksetzen
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {getActiveFilters().map((filter, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                    >
                      {filter.type}: {filter.value}
                      <button onClick={filter.onRemove} className="ml-0.5 hover:opacity-70 transition-opacity">
                        <i className="fas fa-times text-[9px]"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* View Controls */}
            <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 block mb-2.5">
                Ansicht
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  className="inline-flex items-center gap-1.5 h-7 px-3 rounded-xl text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30"
                  onClick={handleExpandAll}
                >
                  <i className="fas fa-expand-alt text-[10px]"></i>
                  Alle ausklappen
                </button>
                <button
                  className="inline-flex items-center gap-1.5 h-7 px-3 rounded-xl text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30"
                  onClick={handleCollapseAll}
                >
                  <i className="fas fa-compress-alt text-[10px]"></i>
                  Alle einklappen
                </button>
                {/* Pill-toggle: Abgeschlossene ausblenden */}
                <button
                  onClick={() => setHideCompleted(prev => !prev)}
                  disabled={selectedStatus === "Abgeschlossen"}
                  className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-xl text-xs font-medium transition-colors border disabled:opacity-50 disabled:cursor-not-allowed ${hideCompleted
                    ? 'bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light border-primary/30 dark:border-primary-light/30'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light'
                  }`}
                >
                  <i className={`fas ${hideCompleted ? 'fa-eye-slash text-primary dark:text-primary-light' : 'fa-eye text-slate-400'} text-[10px]`}></i>
                  Abgeschlossene {hideCompleted ? 'eingeblendet' : 'ausblenden'}
                </button>
              </div>
            </div>
          </div>

          {/* Schedule View */}
          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.entries(displayVenuesData).map(([venue, events]) => (
                <div key={venue} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                  {/* Venue Header */}
                  <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-map-marker-alt text-xs"></i>
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{venue}</h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                              <i className="fas fa-calendar-alt text-[9px]"></i>
                              {events.length} Events
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                              <i className="fas fa-users text-[9px]"></i>
                              {getTotalParticipants(events)} Athleten
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                              <i className="fas fa-clock text-[9px]"></i>
                              {calculateTimeEstimate(events)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Events Timeline */}
                  <div className={`p-5 ${isEditMode ? 'bg-primary/[0.02] dark:bg-primary-light/[0.04]' : ''}`}>
                    {events.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                          <i className="fas fa-calendar-times text-slate-400 dark:text-slate-500 text-2xl"></i>
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Events</h3>
                        <p className="text-slate-500 dark:text-slate-400">{t("admin.schedule.noEvents", "Keine Events an diesem Tag")}</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {events.map((event, index) => {
                          const eventParticipants = getParticipantsForEvent(event);
                          const duration = calculateGroupDurationMinutes(event.startTime, event.endTime);
                          const isLunchBreak = event.isLunchBreak === true;

                          // Prüfe ob Tagwechsel
                          const prevEvent = index > 0 ? events[index - 1] : null;
                          const isNewDay = prevEvent && event.startTime && prevEvent.startTime &&
                            new Date(event.startTime).toDateString() !== new Date(prevEvent.startTime).toDateString();

                          return (
                            <React.Fragment key={event.id}>
                              {/* Tagwechsel-Marker */}
                              {isNewDay && (
                                <div className="flex items-center gap-4 my-6">
                                  <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                  <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700/50 rounded-full border border-slate-200 dark:border-slate-600">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                      {formatDate(event.startTime)}
                                    </span>
                                  </div>
                                  <div className="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                                </div>
                              )}

                              {/* Drag-Indikator VOR dieser Karte */}
                              {isEditMode && draggedEvent && dragOverVenue === venue && dragOverIndex === index && (
                                <div className="flex items-center gap-2 -my-2">
                                  <div className="w-3 h-3 rounded-full bg-primary dark:bg-primary-light border-2 border-white dark:border-slate-800 shadow-sm"></div>
                                  <div className="flex-1 h-0.5 bg-primary dark:bg-primary-light rounded-full"></div>
                                  <div className="w-3 h-3 rounded-full bg-primary dark:bg-primary-light border-2 border-white dark:border-slate-800 shadow-sm"></div>
                                </div>
                              )}

                              <div
                                className={`rounded-2xl shadow-sm overflow-hidden transition-all ${
                                  isLunchBreak
                                    ? 'bg-slate-50 dark:bg-slate-800/60 border-2 border-dashed border-slate-300 dark:border-slate-600'
                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 border-l-4'
                                } ${!isLunchBreak
                                    ? event.status === 'Aktiv' ? 'border-l-emerald-500 dark:border-l-emerald-400 shadow-md'
                                      : event.status === 'Vorbereitung' ? 'border-l-orange-400'
                                      : event.status === 'Ausstehend' ? 'border-l-yellow-400'
                                      : 'border-l-slate-300 dark:border-l-slate-600'
                                    : ''
                                } ${isLunchBreak ? '' : (isEditMode && isEventDraggable(event) ? 'cursor-move hover:shadow-md' : isEditMode ? 'cursor-not-allowed opacity-75' : 'hover:shadow-sm')
                                  } ${event.status === 'Aktiv' && !isLunchBreak ? 'hover:shadow-lg' : ''
                                  } ${draggedEvent?.id === event.id ? 'opacity-50 scale-95' : ''
                                  } ${event.status === 'Abgeschlossen' && !isLunchBreak ? 'opacity-60 grayscale' : ''
                                  }`}
                                draggable={isEditMode && !isLunchBreak && isEventDraggable(event)}
                                onDragStart={isLunchBreak || !isEventDraggable(event) ? undefined : (e) => handleScheduleEventDragStart(e, event, venue, index)}
                                onDragOver={isLunchBreak || !isEventDraggable(event) ? undefined : (e) => handleScheduleEventDragOver(e, venue, index)}
                                onDragLeave={isLunchBreak || !isEventDraggable(event) ? undefined : handleScheduleEventDragLeave}
                                onDrop={isLunchBreak || !isEventDraggable(event) ? undefined : (e) => handleScheduleEventDrop(e, venue, index)}
                                onDragEnd={isLunchBreak || !isEventDraggable(event) ? undefined : handleScheduleEventDragEnd}
                                onContextMenu={(e) => handleContextMenu(e, event, venue, index)}
                              >
                                {isLunchBreak ? (
                                  /* Mittagspause – kompakt */
                                  <div className="px-5 py-3.5 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                      <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-utensils text-slate-600 dark:text-slate-300 text-[10px]"></i>
                                      </div>
                                      <div>
                                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Mittagspause</div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                          {formatTime(event.startTime)} – {formatTime(event.endTime)}
                                        </div>
                                      </div>
                                    </div>
                                    <span className="text-xs text-slate-400 dark:text-slate-500">
                                      {calculateGroupDuration(event.startTime, event.endTime)}
                                    </span>
                                  </div>
                                ) : (
                                  <>
                                    {/* Nicht verschiebbar Hinweis */}
                                    {isEditMode && !isEventDraggable(event) && (
                                      <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                                        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                                          <i className="fas fa-lock text-[10px]"></i>
                                          <span>Event kann nicht verschoben werden (Status: {getStatusLabel(event.status)})</span>
                                        </div>
                                      </div>
                                    )}

                                    <div className="px-4 py-3.5">
                                      {/* Zeile 1: Sequenznummer + Name + Metadaten | Zeit + Dauer */}
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-2 flex-1 min-w-0">
                                          {eventSequenceNumbers[event.id] && (
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-200 flex-shrink-0 mt-0.5">
                                              {eventSequenceNumbers[event.id]}
                                            </span>
                                          )}
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug">
                                              {(() => {
                                                const cat = event.group?.category?.name;
                                                if (cat) return cat;
                                                const full = event.group?.name || 'Unbekanntes Event';
                                                const dash = full.indexOf(' - ');
                                                return dash > 0 ? full.substring(0, dash).trim() : full;
                                              })()}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex-wrap">
                                              <span>{event.group?.ageGroup?.name || 'Unbekannt'}</span>
                                              <span className="text-slate-300 dark:text-slate-600">•</span>
                                              <span className="flex items-center gap-0.5">
                                                <i className={`fas ${event.group?.gender === 'männlich' ? 'fa-mars' : event.group?.gender === 'weiblich' ? 'fa-venus' : 'fa-venus-mars'} text-[9px]`}></i>
                                                {event.group?.gender || 'Unbekannt'}
                                              </span>
                                              {event.group && (
                                                <>
                                                  <span className="text-slate-300 dark:text-slate-600">•</span>
                                                  <span className="flex items-center gap-0.5">
                                                    <i className={`fas ${(event.group.twoParallel ?? false) ? 'fa-users' : 'fa-user'} text-[9px]`}></i>
                                                    {(event.group.twoParallel ?? false) ? "2 parallel" : "Alleine"}
                                                  </span>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 tabular-nums leading-snug">
                                            {formatTime(event.startTime)} – {formatTime(event.endTime)}
                                          </div>
                                          <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                                            {calculateGroupDuration(event.startTime, event.endTime)}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Zeile 2: Status + Teilnehmer | Aktionen */}
                                      <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-700/60">
                                        <div className="flex items-center gap-2">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                                            event.status === 'Ausstehend' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-300/50 dark:border-yellow-600/50' :
                                            event.status === 'Aktiv' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-300/50 dark:border-emerald-600/50' :
                                            event.status === 'Vorbereitung' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-300/50 dark:border-orange-600/50' :
                                            'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600/50'
                                          }`}>
                                            <i className={`fas ${
                                              event.status === 'Aktiv' ? 'fa-play' :
                                              event.status === 'Ausstehend' ? 'fa-clock' :
                                              event.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                                              'fa-check'
                                            } mr-1 text-[9px]`}></i>
                                            {getStatusLabel(event.status)}
                                          </span>
                                          <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                            <i className="fas fa-users text-[9px]"></i>
                                            {(() => {
                                              const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
                                              const isPartnerForm = event.group?.category?.type === 'Partnerform';
                                              const partnerTeamsForGroup = partnerTeamsData.get(event.group?.id);
                                              if (isTeamEvent && teamData && teamData.currentParticipants) return `${teamData.currentParticipants} Teilnehmer`;
                                              if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) return `${partnerTeamsForGroup.partnerTeams.length} Teams`;
                                              return `${eventParticipants.length} Teilnehmer`;
                                            })()}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          {isEditMode && isEventDraggable(event) && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                handleContextMenu(
                                                  { preventDefault: () => {}, clientX: rect.right, clientY: rect.bottom },
                                                  event, venue, index
                                                );
                                              }}
                                              className="inline-flex items-center justify-center w-7 h-7 sm:w-6 sm:h-6 rounded-lg sm:rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                              title="Aktionen"
                                            >
                                              <i className="fas fa-ellipsis-v text-xs"></i>
                                            </button>
                                          )}
                                          <button
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:text-primary dark:hover:text-primary-light hover:border-primary/40 dark:hover:border-primary-light/40"
                                            onClick={() => handleToggleExpand(event.id)}
                                          >
                                            <i className={`fas ${expandedEvents.has(event.id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-[10px]`}></i>
                                            {expandedEvents.has(event.id) ? "Ausblenden" : "Teilnehmer"}
                                          </button>
                                        </div>
                                      </div>

                                      {/* Expanded Participants */}
                                      {expandedEvents.has(event.id) && (
                                        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                                          <span className="block text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2.5">
                                            Teilnehmerliste
                                          </span>
                                          {renderParticipantsList(event.id, eventParticipants)}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </React.Fragment>
                          );
                        })}

                        {/* Drop Zone am Ende der Venue */}
                        {isEditMode && (
                          <div
                            className={`border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-6 text-center transition-colors ${dragOverVenue === venue && dragOverIndex === (isEditMode ? venuesData[venue].length : events.length)
                              ? 'border-primary dark:border-primary-light bg-slate-50 dark:bg-slate-700'
                              : 'hover:border-slate-400 dark:hover:border-slate-500'
                              }`}
                            onDragOver={(e) => handleScheduleEventDragOver(e, venue, isEditMode ? venuesData[venue].length : events.length)}
                            onDragLeave={handleScheduleEventDragLeave}
                            onDrop={(e) => handleScheduleEventDrop(e, venue, isEditMode ? venuesData[venue].length : events.length)}
                          >
                            <i className="fas fa-plus text-slate-400 dark:text-slate-500 text-2xl mb-2"></i>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Event hier ablegen</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
              <div className="px-5 py-4 sm:px-6 border-b border-slate-100 dark:border-slate-700/60">
                <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t('admin.schedule.timeline.title', 'Tages-Timeline')}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                  {t(
                    'admin.schedule.timeline.subtitle',
                    'Fläche A und B auf einer gemeinsamen Zeitachse – parallele Slots auf einen Blick.'
                  )}
                </p>
              </div>

              <div className="p-4 sm:p-5">
                {(() => {
                  const timelineData = getTimelineData();

                  if (timelineData.events.length === 0) {
                    return (
                      <div className="text-center py-14 px-4">
                        <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                          <i className="fas fa-stream text-slate-400 dark:text-slate-500 text-xl" aria-hidden="true"></i>
                        </div>
                        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1.5">
                          {t('admin.schedule.timeline.emptyTitle', 'Keine Events in der Timeline')}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                          {t(
                            'admin.schedule.timeline.emptyHint',
                            'Passe Tag oder Filter an oder wechsle zur Kartenansicht.'
                          )}
                        </p>
                      </div>
                    );
                  }

                  const spanMinutes = Math.max(
                    1,
                    Math.round((timelineData.latestTime - timelineData.earliestTime) / (1000 * 60))
                  );

                  return (
                    <div className="space-y-4">
                      {isEditMode && (
                        <div
                          className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl border border-primary/25 dark:border-primary-light/25 bg-primary/[0.06] dark:bg-primary-light/[0.08]"
                          role="status"
                        >
                          <div className="w-7 h-7 rounded-lg bg-primary/15 dark:bg-primary-light/15 flex items-center justify-center flex-shrink-0 text-primary dark:text-primary-light">
                            <i className="fas fa-arrows-alt text-[11px]" aria-hidden="true"></i>
                          </div>
                          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed pt-0.5">
                            {t(
                              'admin.schedule.timeline.editHint',
                              'Ziehen zum Verschieben, Rechtsklick für Fläche und Position. Mittagspause ist fix.'
                            )}
                          </p>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700/80 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600">
                          <i className="fas fa-clock text-[10px] text-slate-500 dark:text-slate-400" aria-hidden="true"></i>
                          <span className="tabular-nums">
                            {formatTimeForTimeline(timelineData.earliestTime)} –{' '}
                            {formatTimeForTimeline(timelineData.latestTime)}
                          </span>
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700/80 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600">
                          <i className="fas fa-hourglass-half text-[10px] text-slate-500 dark:text-slate-400" aria-hidden="true"></i>
                          {t('admin.schedule.timeline.durationLabel', 'Gesamtdauer')}:{' '}
                          {spanMinutes >= 60
                            ? `${Math.floor(spanMinutes / 60)} h ${spanMinutes % 60} min`
                            : `${spanMinutes} min`}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20">
                          <i className="fas fa-layer-group text-[10px]" aria-hidden="true"></i>
                          {timelineData.events.length}{' '}
                          {t('admin.schedule.timeline.eventsLabel', 'Events gesamt')}
                        </span>
                        {selectedDay && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700/80 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                            <i className="fas fa-calendar-day text-[10px]" aria-hidden="true"></i>
                            {formatDate(selectedDay)}
                          </span>
                        )}
                      </div>

                      <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5 sm:hidden">
                        <i className="fas fa-hand-pointer text-[10px]" aria-hidden="true"></i>
                        {t('admin.schedule.timeline.scrollHint', 'Horizontal wischen, um beide Flächen zu sehen.')}
                      </p>

                      <div
                        className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-900/25 shadow-inner"
                        role="region"
                        aria-label={t('admin.schedule.timeline.regionLabel', 'Zeitplan-Timeline beider Flächen')}
                      >
                        <div className="flex w-full min-w-[720px]">
                          {/* Zeitspalte — Header-Höhe wie Flächen-Header für vertikale Ausrichtung */}
                          <div className="w-[5.25rem] sm:w-28 shrink-0 sticky left-0 z-20 flex flex-col border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 shadow-[4px_0_12px_-6px_rgba(15,23,42,0.12)] dark:shadow-[4px_0_12px_-6px_rgba(0,0,0,0.35)]">
                            <div className="h-12 flex-shrink-0 flex items-center px-2 sm:px-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                {t('admin.schedule.timeline.timeAxis', 'Zeit')}
                              </span>
                            </div>
                            <div
                              className="flex flex-col flex-1 min-h-0"
                              style={{ height: `${timelineData.timelineHeight}px` }}
                            >
                              {(() => {
                                const intervalMinutes = 5;
                                const intervals = [];
                                const startMs = timelineData.earliestTime.getTime();
                                const endMs = timelineData.latestTime.getTime();
                                for (let time = startMs; time <= endMs; time += intervalMinutes * 60 * 1000) {
                                  intervals.push(new Date(time));
                                }
                                return intervals.map((time, i) => {
                                  const m = time.getMinutes();
                                  const showTimeLabel = m % 15 === 0;
                                  const isHour = m === 0;
                                  return (
                                    <div
                                      key={i}
                                      className={`flex-1 flex items-center justify-end pr-2 sm:pr-2.5 border-b border-slate-100 dark:border-slate-700/80 ${
                                        isHour ? 'bg-slate-100/80 dark:bg-slate-700/40' : ''
                                      }`}
                                    >
                                      {showTimeLabel ? (
                                        <span
                                          className={`tabular-nums text-right leading-none ${
                                            isHour
                                              ? 'text-[11px] sm:text-xs font-semibold text-slate-800 dark:text-slate-100'
                                              : 'text-[10px] sm:text-[11px] font-medium text-slate-500 dark:text-slate-400'
                                          }`}
                                        >
                                          {formatTimeForTimeline(time)}
                                        </span>
                                      ) : (
                                        <span
                                          className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600"
                                          aria-hidden="true"
                                        />
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>

                          <div className="flex flex-1 min-w-0">
                            {Object.entries(displayVenuesData).map(([venue, events]) => (
                              <div
                                key={venue}
                                className="flex-1 min-w-0 border-r border-slate-200 dark:border-slate-700 last:border-r-0 bg-white dark:bg-slate-800"
                              >
                                <div className="h-12 flex-shrink-0 px-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-800 flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-lg bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light flex items-center justify-center flex-shrink-0">
                                    <i className="fas fa-map-marker-alt text-[11px]" aria-hidden="true"></i>
                                  </div>
                                  <span className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
                                    {venue}
                                  </span>
                                </div>

                                <div
                                  className="relative"
                                  style={{ height: `${timelineData.timelineHeight}px` }}
                                >
                                  {/* Dezentes Raster: Stundenlinien */}
                                  {(() => {
                                    const lines = [];
                                    const startMs = timelineData.earliestTime.getTime();
                                    const endMs = timelineData.latestTime.getTime();
                                    const dur = timelineData.totalDuration;
                                    if (dur <= 0) return null;
                                    const cursor = new Date(timelineData.earliestTime);
                                    cursor.setMinutes(0, 0, 0);
                                    cursor.setSeconds(0, 0);
                                    if (cursor.getTime() < startMs) {
                                      cursor.setHours(cursor.getHours() + 1);
                                    }
                                    while (cursor.getTime() <= endMs) {
                                      const t = cursor.getTime();
                                      const topPx = ((t - startMs) / dur) * timelineData.timelineHeight;
                                      if (topPx >= 0 && topPx <= timelineData.timelineHeight) {
                                        lines.push(
                                          <div
                                            key={`hour-${t}`}
                                            className="absolute left-0 right-0 pointer-events-none border-t border-slate-200/90 dark:border-slate-600/50 z-[1]"
                                            style={{ top: `${topPx}px` }}
                                          />
                                        );
                                      }
                                      cursor.setHours(cursor.getHours() + 1);
                                    }
                                    return lines;
                                  })()}

                                  {events.map((event, eventIndex) => {
                                    if (eventIndex === 0 || !event.startTime) return null;
                                    const prevEvent = events[eventIndex - 1];
                                    if (!prevEvent?.startTime) return null;
                                    const eventDate = new Date(event.startTime);
                                    const prevEventDate = new Date(prevEvent.startTime);
                                    if (
                                      eventDate.toDateString() !== prevEventDate.toDateString() &&
                                      !event.isLunchBreak
                                    ) {
                                      const dayStart = new Date(eventDate);
                                      dayStart.setHours(0, 0, 0, 0);
                                      const dayStartPositionPx =
                                        ((dayStart.getTime() - timelineData.earliestTime.getTime()) /
                                          timelineData.totalDuration) *
                                        timelineData.timelineHeight;
                                      return (
                                        <div
                                          key={`day-divider-${event.id}`}
                                          className="absolute left-2 right-2 z-10 pointer-events-none"
                                          style={{ top: `${dayStartPositionPx}px` }}
                                        >
                                          <div className="flex items-center gap-3 -translate-y-1/2">
                                            <div className="flex-1 border-t border-slate-200 dark:border-slate-600" />
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 shadow-sm whitespace-nowrap">
                                              <i className="fas fa-calendar-alt text-[10px] text-slate-400" aria-hidden="true"></i>
                                              {formatDate(event.startTime)}
                                            </span>
                                            <div className="flex-1 border-t border-slate-200 dark:border-slate-600" />
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}

                                  {events.map((event, eventIdx) => {
                                    const { topPx, heightPx } = getEventTimelineLayout(event, timelineData);
                                    const eventParticipants = getParticipantsForEvent(event);
                                    const isLunchBreak = event.isLunchBreak === true;
                                    const isTeamEvent = !!(
                                      event.group?.isTeamCompetition ||
                                      event.group?.id === 'team-competition' ||
                                      event.group?.name === 'Gruppenwettbewerb'
                                    );
                                    const isPartnerForm = event.group?.category?.type === 'Partnerform';
                                    const partnerTeamsForGroup = partnerTeamsData.get(event.group?.id);
                                    const participantLabel = (() => {
                                      if (isTeamEvent && teamData && teamData.currentParticipants) {
                                        return `${teamData.currentParticipants} Athleten`;
                                      }
                                      if (isPartnerForm && partnerTeamsForGroup && partnerTeamsForGroup.partnerTeams) {
                                        return `${partnerTeamsForGroup.partnerTeams.length} Teams`;
                                      }
                                      return `${eventParticipants.length} Teilnehmer`;
                                    })();

                                    const categoryTitle = (() => {
                                      const cat = event.group?.category?.name;
                                      if (cat) return cat;
                                      const full = event.group?.name || 'Unbekanntes Event';
                                      const dash = full.indexOf(' - ');
                                      return dash > 0 ? full.substring(0, dash).trim() : full;
                                    })();

                                    const genderDisplay =
                                      event.group?.gender === 'gemischt' ||
                                      event.group?.category?.type === 'Partnerform' ||
                                      event.group?.isTeamCompetition
                                        ? 'Gemischt'
                                        : event.group?.gender === 'männlich'
                                          ? 'Männlich'
                                          : event.group?.gender === 'weiblich'
                                            ? 'Weiblich'
                                            : event.group?.gender || '';

                                    const metaLine = event.group
                                      ? [
                                          event.group.ageGroup?.name || 'Unbekannt',
                                          genderDisplay || 'Unbekannt',
                                          (event.group.twoParallel ?? false) ? '2 parallel' : 'Alleine'
                                        ].join(' · ')
                                      : '';

                                    const timeRange = `${formatTimeForTimeline(new Date(event.startTime))} – ${formatTimeForTimeline(new Date(event.endTime))}`;
                                    const eventTitle = metaLine
                                      ? `${categoryTitle} · ${timeRange} · ${metaLine}`
                                      : `${categoryTitle} · ${timeRange}`;

                                    const statusPillClass = `inline-flex items-center px-2 py-0.5 rounded-full font-medium border shrink-0 ${
                                      event.status === 'Ausstehend'
                                        ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-300/50 dark:border-yellow-600/50'
                                        : event.status === 'Aktiv'
                                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-300/50 dark:border-emerald-600/50'
                                          : event.status === 'Vorbereitung'
                                            ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-300/50 dark:border-orange-600/50'
                                            : 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600/50'
                                    }`;

                                    const doneClass =
                                      event.status === 'Abgeschlossen' && !isLunchBreak
                                        ? 'opacity-60 grayscale'
                                        : '';

                                    const outerInteractive = isLunchBreak
                                      ? 'cursor-default'
                                      : isEditMode && isEventDraggable(event)
                                        ? 'cursor-grab active:cursor-grabbing'
                                        : isEditMode
                                          ? 'cursor-not-allowed opacity-75'
                                          : '';

                                    return (
                                      <div
                                        key={event.id}
                                        title={eventTitle}
                                        className={`absolute overflow-hidden rounded-2xl transition-shadow ${outerInteractive} ${
                                          event.status === 'Aktiv' && !isLunchBreak
                                            ? 'shadow-md ring-2 ring-emerald-500/30 dark:ring-emerald-400/35'
                                            : 'shadow-sm ring-1 ring-slate-200/90 dark:ring-slate-600/70'
                                        } ${draggedEvent?.id === event.id ? 'opacity-50 scale-[0.98]' : ''} hover:shadow-md`}
                                        style={{
                                          top: `${topPx}px`,
                                          height: `${heightPx}px`,
                                          width: 'calc(100% - 10px)',
                                          left: '5px',
                                          zIndex: 4 + eventIdx
                                        }}
                                        draggable={isEditMode && !isLunchBreak && isEventDraggable(event)}
                                        onDragStart={
                                          isLunchBreak || !isEventDraggable(event)
                                            ? undefined
                                            : (e) =>
                                                handleScheduleEventDragStart(
                                                  e,
                                                  event,
                                                  venue,
                                                  events.indexOf(event)
                                                )
                                        }
                                        onDragOver={
                                          isLunchBreak || !isEventDraggable(event)
                                            ? undefined
                                            : (e) =>
                                                handleScheduleEventDragOver(e, venue, events.indexOf(event))
                                        }
                                        onDragLeave={
                                          isLunchBreak || !isEventDraggable(event)
                                            ? undefined
                                            : handleScheduleEventDragLeave
                                        }
                                        onDrop={
                                          isLunchBreak || !isEventDraggable(event)
                                            ? undefined
                                            : (e) =>
                                                handleScheduleEventDrop(e, venue, events.indexOf(event))
                                        }
                                        onDragEnd={
                                          isLunchBreak || !isEventDraggable(event)
                                            ? undefined
                                            : handleScheduleEventDragEnd
                                        }
                                        onContextMenu={(e) =>
                                          handleContextMenu(e, event, venue, events.indexOf(event))
                                        }
                                      >
                                        {isLunchBreak ? (
                                          <div className="h-full flex items-center gap-2.5 px-3 py-2 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/60 min-w-0 overflow-hidden">
                                            <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center shrink-0">
                                              <i
                                                className="fas fa-utensils text-slate-600 dark:text-slate-300 text-[10px]"
                                                aria-hidden="true"
                                              ></i>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                              <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 leading-tight truncate">
                                                {t('admin.schedule.lunchBreak', 'Mittagspause')}
                                              </div>
                                              <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums leading-tight mt-0.5 truncate">
                                                {timeRange}
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className={`h-full flex flex-col min-h-0 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 border-l-4 ${timelineStatusBorderL(event.status)} shadow-sm px-3 py-2 gap-1.5 ${doneClass}`}
                                          >
                                            <div className="flex items-start justify-between gap-2 shrink-0 min-h-0">
                                              <span className="text-xs font-semibold text-slate-900 dark:text-slate-100 leading-snug truncate min-w-0 flex-1">
                                                {categoryTitle}
                                              </span>
                                              <span className="text-xs font-semibold text-slate-900 dark:text-slate-100 tabular-nums leading-snug whitespace-nowrap shrink-0 text-right">
                                                {formatTimeForTimeline(new Date(event.startTime))} –{' '}
                                                {formatTimeForTimeline(new Date(event.endTime))}
                                              </span>
                                            </div>
                                            {metaLine ? (
                                              <p
                                                className="shrink-0 text-xs text-slate-500 dark:text-slate-400 leading-snug line-clamp-2 break-words min-h-[2.5rem]"
                                                title={metaLine}
                                              >
                                                {metaLine}
                                              </p>
                                            ) : (
                                              <p className="shrink-0 text-xs text-slate-400 dark:text-slate-500 italic min-h-[2.5rem] leading-snug">
                                                {t('admin.schedule.timeline.noGroupMeta', 'Keine Gruppendaten')}
                                              </p>
                                            )}
                                            <div className="flex items-center gap-2 shrink-0 pt-1.5 mt-auto border-t border-slate-100 dark:border-slate-700/60 min-h-0 overflow-hidden">
                                              <span className={`${statusPillClass} text-xs py-0.5`}>
                                                <i
                                                  className={`fas ${
                                                    event.status === 'Aktiv'
                                                      ? 'fa-play'
                                                      : event.status === 'Ausstehend'
                                                        ? 'fa-clock'
                                                        : event.status === 'Vorbereitung'
                                                          ? 'fa-hourglass-half'
                                                          : 'fa-check'
                                                  } mr-1 text-[9px]`}
                                                  aria-hidden="true"
                                                ></i>
                                                <span className="truncate max-w-[7rem] sm:max-w-none">
                                                  {getStatusLabel(event.status)}
                                                </span>
                                              </span>
                                              <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 min-w-0 truncate">
                                                <i className="fas fa-users text-[9px] shrink-0" aria-hidden="true"></i>
                                                <span className="truncate">{participantLabel}</span>
                                              </span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmGenerate}
        title={hasExistingEvents ? "Zeitplan neu erstellen" : "Zeitplan erstellen"}
        subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
        message={hasExistingEvents
          ? "Möchten Sie den Zeitplan wirklich neu erstellen? Alle bestehenden Events werden gelöscht und basierend auf den aktuellen Settings neu generiert."
          : "Möchten Sie einen neuen Zeitplan basierend auf den aktuellen Settings erstellen?"}
        confirmText={hasExistingEvents ? "Neu erstellen" : "Erstellen"}
        cancelText="Abbrechen"
        type="warning"
        layout="inline"
        isLoading={isGenerating}
      />

      {/* Bestätigungsdialog: Edit-Mode mit ungespeicherten Änderungen beenden */}
      <ConfirmationModal
        isOpen={showCancelEditConfirmation}
        onClose={() => setShowCancelEditConfirmation(false)}
        onConfirm={handleConfirmCancelEdit}
        title="Bearbeitung abbrechen"
        subtitle={`${changeHistory.length} ungespeicherte ${changeHistory.length === 1 ? 'Änderung' : 'Änderungen'} vorhanden.`}
        message="Möchten Sie die Bearbeitung wirklich abbrechen? Alle ungespeicherten Änderungen gehen verloren."
        confirmText="Verwerfen"
        cancelText="Weiter bearbeiten"
        type="warning"
        layout="inline"
      />

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden"
          style={{
            top: `${Math.min(contextMenu.y, window.innerHeight - 300)}px`,
            left: `${Math.min(contextMenu.x, window.innerWidth - 220)}px`,
            minWidth: '210px'
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseLeave={() => {
            if (contextMenuSubmenu !== 'day-change' || !contextMenuDaySubmenu) {
              setContextMenuSubmenu(null);
              setContextMenuDaySubmenu(null);
            }
          }}
        >
          {/* Context menu header */}
          <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-700/50">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Event verschieben</span>
          </div>
          <div className="py-1">
            {/* Fläche wechseln */}
            <div
              className="relative"
              onMouseEnter={() => { setContextMenuSubmenu('venue-change'); setContextMenuDaySubmenu(null); }}
              onMouseLeave={() => {
                setTimeout(() => {
                  const submenu = document.querySelector('.context-submenu-venue');
                  if (!submenu || !submenu.matches(':hover')) setContextMenuSubmenu(null);
                }, 150);
              }}
            >
              <button className="w-full px-3.5 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center justify-between gap-3 transition-colors" onClick={(e) => e.stopPropagation()}>
                <span className="flex items-center gap-2.5">
                  <i className="fas fa-exchange-alt text-slate-400 dark:text-slate-500 text-xs w-4"></i>
                  Fläche wechseln
                </span>
                <i className="fas fa-chevron-right text-slate-300 dark:text-slate-600 text-[10px]"></i>
              </button>
              {contextMenuSubmenu === 'venue-change' && (
                <div
                  className="context-submenu-venue absolute left-full top-0 ml-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl min-w-[180px] z-[60] overflow-hidden"
                  onMouseEnter={() => setContextMenuSubmenu('venue-change')}
                  onMouseLeave={() => { setTimeout(() => setContextMenuSubmenu(null), 150); }}
                >
                  <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-700/50">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                      → {contextMenu.venue === 'Fläche A' ? 'Fläche B' : 'Fläche A'}
                    </span>
                  </div>
                  <div className="py-1">
                    {[['top','fa-arrow-up','Nach oben'],['bottom','fa-arrow-down','Nach unten'],['after-lunch','fa-utensils','Nach Mittagspause'],['same-index','fa-equals','Gleicher Index']].map(([action, icon, label]) => (
                      <button key={action} className="w-full px-3.5 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors"
                        onClick={() => handleMoveEvent(action, contextMenu.venue === 'Fläche A' ? 'Fläche B' : 'Fläche A')}>
                        <i className={`fas ${icon} text-slate-400 dark:text-slate-500 text-xs w-4`}></i>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="my-1 border-t border-slate-100 dark:border-slate-700/60"></div>

            {/* Nach oben / unten */}
            {[['up','fa-arrow-up','Nach oben'],['down','fa-arrow-down','Nach unten']].map(([action, icon, label]) => (
              <button key={action} className="w-full px-3.5 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors"
                onClick={() => handleMoveEvent(action)}>
                <i className={`fas ${icon} text-slate-400 dark:text-slate-500 text-xs w-4`}></i>
                {label}
              </button>
            ))}

            <div className="my-1 border-t border-slate-100 dark:border-slate-700/60"></div>

            {/* Tag ändern */}
            <div className="relative" onMouseEnter={() => setContextMenuSubmenu('day-change')}>
              <button className="w-full px-3.5 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center justify-between gap-3 transition-colors">
                <span className="flex items-center gap-2.5">
                  <i className="fas fa-calendar-alt text-slate-400 dark:text-slate-500 text-xs w-4"></i>
                  Tag ändern
                </span>
                <i className="fas fa-chevron-right text-slate-300 dark:text-slate-600 text-[10px]"></i>
              </button>
              {contextMenuSubmenu === 'day-change' && (
                <div
                  className="context-submenu-day absolute left-full top-0 ml-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl min-w-[200px] z-[60] overflow-hidden"
                  style={{ maxHeight: dayOptions.length > 6 ? '300px' : 'none', overflowY: dayOptions.length > 6 ? 'auto' : 'visible' }}
                  onMouseEnter={() => setContextMenuSubmenu('day-change')}
                  onMouseLeave={() => { setTimeout(() => { if (!contextMenuDaySubmenu) setContextMenuSubmenu(null); }, 200); }}
                >
                  <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-700/50">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Tag wählen</span>
                  </div>
                  <div className="py-1">
                    {dayOptions.map((dayOption) => dayOption.value !== selectedDay && (
                      <div key={dayOption.value} className="relative" onMouseEnter={() => setContextMenuDaySubmenu(dayOption.value)}>
                        <button className="w-full px-3.5 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center justify-between gap-2 whitespace-nowrap transition-colors">
                          <span>{dayOption.label}</span>
                          <i className="fas fa-chevron-right text-slate-300 dark:text-slate-600 text-[10px]"></i>
                        </button>
                        {contextMenuDaySubmenu === dayOption.value && (
                          <div
                            className="absolute left-full top-0 ml-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl min-w-[180px] z-[80] overflow-hidden"
                            onMouseEnter={() => setContextMenuDaySubmenu(dayOption.value)}
                            onMouseLeave={() => { setTimeout(() => setContextMenuDaySubmenu(null), 200); }}
                          >
                            <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-700/50">
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Position</span>
                            </div>
                            <div className="py-1">
                              {[['top','fa-arrow-up','Nach oben'],['bottom','fa-arrow-down','Nach unten'],['after-lunch','fa-utensils','Nach Mittagspause'],['same-index','fa-equals','Gleicher Index']].map(([action, icon, label]) => (
                                <button key={action} className="w-full px-3.5 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 flex items-center gap-2.5 transition-colors"
                                  onClick={() => handleMoveEvent(action, null, dayOption.value)}>
                                  <i className={`fas ${icon} text-slate-400 dark:text-slate-500 text-xs w-4`}></i>
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close context menu */}
      {contextMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleCloseContextMenu}
          onContextMenu={(e) => {
            e.preventDefault();
            handleCloseContextMenu();
          }}
        />
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
      />
    </>
  );
};

export default ScheduleTailwind;
